import { Controller } from "@hotwired/stimulus";

const MODE_COPY = {
  standard: {
    number: "01",
    title: "STANDARD EXTRA",
    copy: "Draw a collectible, set its value, and choose how people can use it."
  },
  airdrop: {
    number: "02",
    title: "AIRDROP EXTRA",
    copy: "Fund a giveaway from one of your MAZA accounts."
  },
  membership: {
    number: "03",
    title: "MEMBERSHIP EXTRA",
    copy: "Offer one-time or monthly access, paid to your MAZA account."
  }
};

export default class extends Controller {
  connect() {
    this.form = this.element.closest("form");
    this.mode = this.element.querySelector("[data-extra-airdrop-mode]");
    this.airdropPanel = this.element.querySelector("[data-extra-airdrop-panel]");
    this.membershipPanel = this.element.querySelector("[data-extra-membership-panel]");
    this.standardPanel = this.form?.querySelector("[data-extra-standard-panel]");
    this.delivery = this.element.querySelector("[data-airdrop-delivery]");
    this.frequencyRow = this.element.querySelector("[data-extra-airdrop-frequency]");
    this.frequency = this.element.querySelector("[data-airdrop-frequency-input]");
    this.units = this.element.querySelector("[data-airdrop-units]");
    this.count = this.element.querySelector("[data-airdrop-count]");
    this.reserve = this.element.querySelector("[data-airdrop-reserve]");
    this.reserveDetails = this.element.querySelector("[data-airdrop-reserve-details]");
    this.reserveLink = this.element.querySelector("[data-airdrop-reserve-link]");
    this.equation = this.element.querySelector("[data-extra-airdrop-equation]");
    this.membershipBilling = this.element.querySelector("[data-membership-billing]");
    this.membershipPrice = this.element.querySelector("[data-membership-price-maza]");
    this.membershipPayout = this.element.querySelector("[data-membership-payout]");
    this.membershipAvailable = this.element.querySelector("[data-membership-available]");
    this.membershipPayoutDetails = this.element.querySelector("[data-membership-payout-details]");
    this.membershipSummary = this.element.querySelector("[data-membership-summary]");
    this.membershipMonthlyNote = this.element.querySelector("[data-membership-monthly-note]");
    this.modeNumber = this.element.querySelector("[data-extra-mode-number]");
    this.modeTitle = this.element.querySelector("[data-extra-mode-title]");
    this.modeCopy = this.element.querySelector("[data-extra-mode-copy]");

    this.decimals = Number.parseInt(this.element.dataset.thingDecimals || "0", 10);
    this.scale = 10n ** BigInt(this.decimals);
    this.mazaScale = 100000000n;
    this.syncMode();
  }

  change(event) {
    const target = event.target;

    if (target === this.mode) {
      this.syncMode();
    } else if (target === this.delivery) {
      this.syncDelivery();
    } else if (target === this.reserve) {
      this.renderReserve();
    } else if (
      target === this.membershipBilling ||
      target === this.membershipPayout ||
      target === this.membershipAvailable
    ) {
      this.renderMembership();
    }
  }

  input(event) {
    const target = event.target;

    if (target === this.units || target === this.count) {
      this.renderEquation();
    } else if (target === this.membershipPrice) {
      this.renderMembership();
    }
  }

  syncMode() {
    const isAirdrop = this.mode?.value === "AIRDROP";
    const isMembership = this.mode?.value === "MEMBERSHIP";
    const activeMode = isAirdrop ? "airdrop" : (isMembership ? "membership" : "standard");

    this.element.dataset.extraMode = activeMode;
    if (this.form) this.form.dataset.extraMode = activeMode;
    this.renderModeCopy(activeMode);

    this.setPanel(this.airdropPanel, isAirdrop);
    this.setPanel(this.membershipPanel, isMembership);
    this.setPanel(this.standardPanel, !isAirdrop && !isMembership);
    this.syncDelivery();

    if (isAirdrop) {
      this.renderEquation();
      this.renderReserve();
    } else {
      this.units?.setCustomValidity("");
      this.count?.setCustomValidity("");
    }

    if (isMembership) {
      this.renderMembership();
    } else {
      this.membershipPrice?.setCustomValidity("");
    }

    this.element.dispatchEvent(new CustomEvent("extra-builder:mode", {
      bubbles: true,
      detail: { mode: activeMode }
    }));
  }

  renderModeCopy(activeMode) {
    const copy = MODE_COPY[activeMode];
    if (!copy) return;

    this.write(this.modeNumber, copy.number);
    this.write(this.modeTitle, copy.title);
    this.write(this.modeCopy, copy.copy);
  }

  setPanel(panel, active) {
    if (!panel) return;

    panel.hidden = !active;
    if ("disabled" in panel) panel.disabled = !active;
    panel.setAttribute("aria-hidden", String(!active));
    panel.querySelectorAll("input, select, textarea, button").forEach((input) => {
      const locked =
        input.dataset.airdropLocked === "true" || input.dataset.membershipLocked === "true";
      input.disabled = !active || locked;
    });
  }

  syncDelivery() {
    const airdropActive = this.mode?.value === "AIRDROP";
    const classic = this.delivery?.value === "CLASSIC";
    if (this.frequencyRow) this.frequencyRow.hidden = !classic;

    if (this.frequency) {
      const locked = this.frequency.dataset.airdropLocked === "true";
      this.frequency.disabled = !airdropActive || !classic || locked;
      this.frequency.required = airdropActive && classic && !locked;
    }
  }

  renderEquation() {
    if (!this.equation) return;
    const ticker = this.element.dataset.thingTicker || "THING";
    this.units?.setCustomValidity("");
    this.count?.setCustomValidity("");

    if (!this.units?.value || !this.count?.value) {
      this.equation.textContent = "Enter the amount and count to see the funding needed.";
      return;
    }

    try {
      const price = this.parsePositiveDecimal(this.element.dataset.mazaPrice);
      const thingAtoms = this.parseThingAtoms(this.units?.value);
      const pigeons = this.parseUnsignedInteger(this.count?.value);
      if (pigeons <= 0n) throw new Error("positive");
      if (this.count?.max && pigeons > BigInt(this.count.max)) {
        const message = `Choose no more than ${this.count.max} pigeons.`;
        this.count.setCustomValidity(message);
        this.equation.textContent = message;
        return;
      }

      const totalThingAtoms = thingAtoms * pigeons;
      const atomicSupply = this.parseUnsignedInteger(
        this.element.dataset.thingAtomicSupply || "0"
      );
      if (this.decimals === 18 && atomicSupply > 0n && totalThingAtoms > atomicSupply) {
        const message = "This airdrop exceeds the THING's total supply. Reduce the amount or count.";
        this.count?.setCustomValidity(message);
        this.equation.textContent = message;
        return;
      }

      const payoutNumerator = thingAtoms * price.numerator * this.mazaScale;
      const payoutDenominator = this.scale * price.denominator;
      if (payoutNumerator % payoutDenominator !== 0n) {
        const message = "Choose an amount whose MAZA payout has no more than 8 decimal places.";
        this.units?.setCustomValidity(message);
        this.equation.textContent = message;
        return;
      }

      const onePigeonAtoms = payoutNumerator / payoutDenominator;
      if (onePigeonAtoms <= 0n) throw new Error("positive");
      const fullDropAtoms = onePigeonAtoms * pigeons;
      const thingLabel = this.formatAtomic(thingAtoms, this.decimals);

      this.equation.textContent =
        `${thingLabel} $${ticker} = ${this.formatAtomic(onePigeonAtoms, 8)} MAZA per pigeon. ` +
        `${pigeons} pigeons need ${this.formatAtomic(fullDropAtoms, 8)} MAZA + network fees.`;
    } catch (_error) {
      this.equation.textContent =
        "Enter a positive THING amount and a whole-number pigeon count.";
      this.units?.setCustomValidity(this.equation.textContent);
    }
  }

  renderReserve() {
    if (!this.reserveDetails) return;
    const option = this.reserve?.selectedOptions?.[0];
    if (this.reserveLink) {
      this.reserveLink.hidden = !option?.value || !option.dataset.reserveUrl;
      if (!this.reserveLink.hidden) this.reserveLink.href = option.dataset.reserveUrl;
      else this.reserveLink.removeAttribute("href");
    }

    if (!option?.value) {
      this.reserveDetails.textContent =
        "Choose the account that will fund this airdrop.";
      return;
    }

    this.reserveDetails.textContent =
      `${option.dataset.reserveMemo} · ${option.dataset.reserveAddress} · ` +
      `Last known balance: ${option.dataset.reserveBalance} MAZA.`;
  }

  renderMembership() {
    if (!this.membershipSummary) return;
    const selectedPayout = this.membershipPayout?.selectedOptions?.[0];
    if (this.membershipMonthlyNote) {
      this.membershipMonthlyNote.hidden = this.membershipBilling?.value !== "MONTHLY";
    }

    if (this.membershipPayoutDetails) {
      this.membershipPayoutDetails.textContent = selectedPayout?.value
        ? `Payments go to ${selectedPayout.dataset.membershipMemo} · ` +
          `${selectedPayout.dataset.membershipAddress}.`
        : "Choose the account that will receive membership payments.";
    }

    try {
      const atoms = this.parseMazaAtoms(this.membershipPrice?.value);
      this.membershipPrice?.setCustomValidity("");

      const cadence = this.membershipBilling?.value === "MONTHLY" ? "per month" : "one time";
      const payoutState = selectedPayout?.value ? "" : " Choose a receiving account.";
      const availabilityState = this.membershipAvailable?.checked ? "Accepting members" : "Payments paused";
      this.membershipSummary.textContent =
        `${this.formatAtomic(atoms, 8)} MAZA ${cadence}. ${availabilityState}.${payoutState}`;
    } catch (_error) {
      this.membershipPrice?.setCustomValidity(
        "Enter a positive MAZA price with no more than 8 decimal places."
      );
      this.membershipSummary.textContent =
        "Enter a positive MAZA price with no more than 8 decimal places.";
    }
  }

  parseUnsignedInteger(value) {
    const raw = String(value || "").trim();
    if (!/^\d+$/.test(raw)) throw new Error("whole-number");
    return BigInt(raw);
  }

  parsePositiveDecimal(value) {
    const raw = String(value || "").trim();
    const match = raw.match(/^(\d+)(?:\.(\d+))?$/);
    if (!match) throw new Error("decimal");

    const fraction = match[2] || "";
    const denominator = 10n ** BigInt(fraction.length);
    const numerator = (BigInt(match[1]) * denominator) + BigInt(fraction || "0");
    if (numerator <= 0n) throw new Error("positive");
    return { numerator, denominator };
  }

  parseThingAtoms(value) {
    const raw = String(value || "").trim();
    const match = raw.match(/^(\d+)(?:\.(\d+))?$/);
    if (!match) throw new Error("amount");

    const meaningfulFraction = (match[2] || "").replace(/0+$/, "");
    if (meaningfulFraction.length > this.decimals) throw new Error("precision");

    const paddedFraction = meaningfulFraction.padEnd(this.decimals, "0") || "0";
    const atoms = (BigInt(match[1]) * this.scale) + BigInt(paddedFraction);
    if (atoms <= 0n) throw new Error("positive");
    return atoms;
  }

  parseMazaAtoms(value) {
    const raw = String(value || "").trim();
    const match = raw.match(/^(\d+)(?:\.(\d{1,8}))?$/);
    if (!match) throw new Error("maza");

    const fraction = (match[2] || "").padEnd(8, "0");
    const atoms = (BigInt(match[1]) * this.mazaScale) + BigInt(fraction || "0");
    if (atoms <= 0n) throw new Error("positive");
    return atoms;
  }

  formatAtomic(atoms, precision) {
    const localScale = 10n ** BigInt(precision);
    const whole = atoms / localScale;
    if (precision === 0) return whole.toString();

    const fraction = (atoms % localScale)
      .toString()
      .padStart(precision, "0")
      .replace(/0+$/, "");
    return fraction ? `${whole}.${fraction}` : whole.toString();
  }

  write(target, value) {
    if (target) target.textContent = value;
  }
}
